import { Eye, Printer, ChevronLeft, ChevronRight, ArrowUpDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { STATUS_CONFIG, formatCurrency, formatDateIST, formatTimeIST, formatMode, formatDoctor } from "./pharmacyConstants";

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: String(status), color: "text-slate-500", bg: "bg-slate-50", border: "border-slate-200" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.bg} ${cfg.color} border ${cfg.border}`} data-testid={`status-badge-${status}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} /> {cfg.label}
    </span>
  );
}

export function PharmacyOrdersTable({ orders, page, setPage, sortBy, handleSort, loading, summary, openDrillDown, handleCreditBill }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="orders-table-section">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-800">Orders ({orders.total})</h2>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          Page {orders.page} of {orders.total_pages || 1}
          <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page"><ChevronLeft className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="ghost" disabled={page >= orders.total_pages} onClick={() => setPage(p => p + 1)} data-testid="next-page"><ChevronRight className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              {[
                { key: "createdAt", label: "Date & Time" },
                { key: "orderId", label: "Order ID" },
                { key: "patientType", label: "Type" },
                { key: "mrn", label: "MRN" },
                { key: "patientName", label: "Patient" },
                { key: "doctorName", label: "Doctor" },
                { key: "schemeName", label: "Scheme" },
                { key: "grandTotal", label: "Grand Total" },
                { key: "cancelledAmount", label: "Cancelled" },
                { key: "refundedAmount", label: "Refunded" },
                { key: "medicineCount", label: "Meds" },
                { key: "status", label: "Status" },
                { key: null, label: "Payment Mode" },
                { key: null, label: "" },
              ].map(col => (
                <TableHead key={col.label} className={`text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap ${col.key ? "cursor-pointer hover:text-slate-700" : ""}`}
                  onClick={() => col.key && handleSort(col.key)}>
                  <span className="flex items-center gap-1">
                    {col.label}
                    {col.key && sortBy === col.key && <ArrowUpDown className="w-3 h-3 text-teal-600" />}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={14} className="text-center py-10 text-slate-400">Loading...</TableCell></TableRow>}
            {!loading && orders.data.length === 0 && <TableRow><TableCell colSpan={14} className="text-center py-10 text-slate-400">No orders found</TableCell></TableRow>}
            {!loading && orders.data.map((o, i) => (
              <TableRow key={o.orderId || i} className="hover:bg-slate-50 transition-colors" data-testid={`order-row-${o.orderId}`}>
                <TableCell className="text-xs whitespace-nowrap">
                  <div>{formatDateIST(o.createdAt)}</div>
                  <div className="text-[10px] text-slate-400">{formatTimeIST(o.createdAt)}</div>
                </TableCell>
                <TableCell className="text-xs font-mono font-semibold text-slate-700">{o.orderId}</TableCell>
                <TableCell className="text-xs">{o.patientType}</TableCell>
                <TableCell className="text-xs font-mono">{o.mrn}</TableCell>
                <TableCell className="text-xs max-w-[150px] truncate">{o.patientName}</TableCell>
                <TableCell className="text-xs max-w-[120px] truncate">{formatDoctor(o.doctorName)}</TableCell>
                <TableCell className="text-xs max-w-[100px] truncate">{o.schemeName || "\u2014"}</TableCell>
                <TableCell className={`text-xs text-right font-semibold tabular-nums ${o.grandTotal < 0 ? "text-red-600" : ""}`}>{formatCurrency(o.grandTotal)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums text-slate-500">{o.cancelledAmount ? formatCurrency(o.cancelledAmount) : "\u2014"}</TableCell>
                <TableCell className="text-xs text-right tabular-nums text-red-600">{o.refundedAmount ? formatCurrency(o.refundedAmount) : "\u2014"}</TableCell>
                <TableCell className="text-xs text-center">{o.medicineCount}</TableCell>
                <TableCell><StatusBadge status={o.status} /></TableCell>
                <TableCell className="text-xs" data-testid={`payment-mode-${o.orderId}`}>
                  {o.paymentModes && o.paymentModes.length > 0
                    ? o.paymentModes.map(formatMode).join(", ")
                    : <span className="text-slate-300">{"\u2014"}</span>}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openDrillDown(o.orderId)} data-testid={`drill-${o.orderId}`}>
                      <Eye className="w-3.5 h-3.5 text-slate-400" />
                    </Button>
                    {o.paymentModes && o.paymentModes.includes("CREDIT") && (
                      <Button variant="ghost" size="sm" onClick={() => handleCreditBill(o.orderId)} data-testid={`credit-bill-${o.orderId}`} title="Print Credit Bill">
                        <Printer className="w-3.5 h-3.5 text-teal-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!loading && orders.data.length > 0 && (() => {
              const pageTotal = orders.data.reduce((s, o) => s + (o.grandTotal || 0), 0);
              const pageCancelled = orders.data.reduce((s, o) => s + (o.cancelledAmount || 0), 0);
              const pageRefunded = orders.data.reduce((s, o) => s + (o.refundedAmount || 0), 0);
              const pageMeds = orders.data.reduce((s, o) => s + (o.medicineCount || 0), 0);
              return (
                <>
                  <TableRow className="bg-slate-100 border-t-2 border-slate-300" data-testid="page-summary-row">
                    <TableCell colSpan={7} className="text-xs font-bold text-slate-700 text-right">Page Total ({orders.data.length} orders)</TableCell>
                    <TableCell className="text-xs text-right font-bold tabular-nums text-slate-800">{formatCurrency(pageTotal)}</TableCell>
                    <TableCell className="text-xs text-right font-bold tabular-nums text-slate-500">{pageCancelled > 0 ? formatCurrency(pageCancelled) : "\u2014"}</TableCell>
                    <TableCell className="text-xs text-right font-bold tabular-nums text-red-600">{pageRefunded > 0 ? formatCurrency(pageRefunded) : "\u2014"}</TableCell>
                    <TableCell className="text-xs text-center font-bold text-slate-800">{pageMeds}</TableCell>
                    <TableCell colSpan={3}></TableCell>
                  </TableRow>
                  {orders.total_pages > 1 && (
                    <TableRow className="bg-teal-50 border-t border-teal-200" data-testid="grand-summary-row">
                      <TableCell colSpan={7} className="text-xs font-bold text-teal-800 text-right">Grand Total ({summary.total_orders} orders)</TableCell>
                      <TableCell className="text-xs text-right font-bold tabular-nums text-teal-800">{formatCurrency(summary.total_revenue)}</TableCell>
                      <TableCell className="text-xs text-right font-bold tabular-nums text-slate-500">{(summary.cancelled_amount || 0) > 0 ? formatCurrency(summary.cancelled_amount) : "\u2014"}</TableCell>
                      <TableCell className="text-xs text-right font-bold tabular-nums text-red-600">{(summary.refunded_amount || 0) > 0 ? formatCurrency(summary.refunded_amount) : "\u2014"}</TableCell>
                      <TableCell className="text-xs text-center font-bold text-teal-800">{summary.total_medicines || "\u2014"}</TableCell>
                      <TableCell colSpan={3}></TableCell>
                    </TableRow>
                  )}
                </>
              );
            })()}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
